<div class="admin-stats-dashboard flex h-full min-h-0 w-full flex-col overflow-hidden">
  <div class="shrink-0 p-5">
    <h4 class="m-0 text-3xl font-bold tracking-tight text-zinc-900 dark:text-white">Account mode</h4>
    @if (rangeLabel()) {
    <p class="m-0 mt-2 max-w-4xl text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
      Daily unique visits in the SHOUT app by creator, brand, and agency —
      {{ rangeLabel() }}. Snapshots run overnight, so the latest day is yesterday.
    </p>
    } @else {
    <p class="m-0 mt-2 max-w-4xl text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
      Daily unique visits in the SHOUT app by creator, brand, and agency. Snapshots
      run overnight, so the latest day is yesterday.
    </p>
    }
  </div>

  <admin-stats-period-filter [maxEndDate]="latestSnapshotDate" (periodChange)="onPeriodChange($event)" />

  @if (loadingState() === 'error' && error()) {
  <div
    class="mx-5 mt-4 shrink-0 rounded-xl border border-rose-200 bg-rose-50 p-3 dark:border-rose-500/30 dark:bg-rose-500/10">
    <p class="m-0 text-sm text-rose-700 dark:text-rose-300">{{ error() }}</p>
  </div>
  }

  <div class="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-5 pb-5 scrollbar-transparent">
    @if (loadingState() === 'loading') {
    <atom-loading-inline title="Loading Google Analytics" message="Fetching daily snapshots…" />
    } @else if (report()) {
    <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4 mt-5">
      @for (card of kpiCards(); track card.key) {
      <section class="admin-stats-panel rounded-2xl p-5">
        <div class="flex items-center gap-2">
          <span class="size-2 rounded-full" [class]="kpiAccentDotClass(card.accent)" aria-hidden="true"></span>
          <p class="m-0 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
            {{ card.label }}
          </p>
        </div>
        <p class="m-0 mt-4 text-4xl font-bold leading-none tabular-nums text-zinc-900 dark:text-white xl:text-5xl">
          {{ card.current.toLocaleString() }}
        </p>
        @let trend = getKpiTrend(card.current, card.previous);
        <p class="m-0 mt-4 text-sm">
          <span class="font-semibold" [class]="trend.trendClass">{{ trend.arrow }} {{ trend.percentLabel }}</span>
          <span class="text-zinc-500 dark:text-zinc-500"> {{ trend.comparisonLabel }}</span>
        </p>
        <p class="m-0 mt-2 text-xs tabular-nums text-zinc-500">
          {{ card.events.toLocaleString() }} events
        </p>
      </section>
      }
    </div>

    <div class="grid gap-4 md:grid-cols-2">
      @for (card of platformKpiCards(); track card.key) {
      <section class="admin-stats-panel rounded-2xl p-5 relative">
        <div class="flex items-center gap-2">
          <span class="size-2 rounded-full" [class]="platformKpiAccentDotClass(card.accent)" aria-hidden="true"></span>
          <p class="m-0 text-xs font-semibold tracking-[0.18em] text-zinc-500 dark:text-zinc-400"
            [class.uppercase]="card.key !== 'iOS'">
            {{ card.label }}
          </p>
        </div>
        <p class="m-0 mt-4 text-4xl font-bold leading-none tabular-nums text-zinc-900 dark:text-white xl:text-5xl">
          {{ card.current.toLocaleString() }}
        </p>
        @let platformTrend = getKpiTrend(card.current, card.previous);
        <p class="m-0 mt-4 text-sm">
          <span class="font-semibold" [class]="platformTrend.trendClass">
            {{ platformTrend.arrow }} {{ platformTrend.percentLabel }}
          </span>
          <span class="text-zinc-500 dark:text-zinc-500"> {{ platformTrend.comparisonLabel }}</span>
        </p>
        <p class="m-0 mt-2 text-xs tabular-nums text-zinc-500">
          {{ card.events.toLocaleString() }} events
        </p>
        <div class="flex animate-fade-in absolute bottom-3 right-3">
          @if (card.key === 'Android') {
          <img src="https://shout-ugc-assets.s3.eu-west-1.amazonaws.com/other/android-logo.png" alt="Android"
            class="size-10">
          } @else if (card.key === 'iOS') {
          <img src="https://shout-ugc-assets.s3.eu-west-1.amazonaws.com/other/apple-logo.png" alt="iOS"
            class="size-10 invert dark:invert-0">
          }
        </div>
      </section>
      }
    </div>

    <div class="grid gap-4 lg:grid-cols-2">
      <section class="admin-stats-panel rounded-2xl p-5">
        <div class="mb-4 flex items-baseline justify-between gap-2">
          <h5 class="m-0 text-base font-semibold text-zinc-900 dark:text-white">Account mix</h5>
          <p class="m-0 text-xs font-medium uppercase tracking-[0.16em] text-zinc-500">Visits</p>
        </div>
        @if (pieHasData()) {
        @if (pieOption(); as accountPie) {
        <div echarts [options]="accountPie" class="block h-80 w-full"></div>
        }
        } @else {
        <div
          class="flex h-80 items-center justify-center rounded-xl border border-dashed border-zinc-200 dark:border-white/10">
          <p class="m-0 text-sm text-zinc-500">No account mode data for this period yet.</p>
        </div>
        }
      </section>

      <section class="admin-stats-panel rounded-2xl p-5">
        <div class="mb-4 flex items-baseline justify-between gap-2">
          <h5 class="m-0 text-base font-semibold text-zinc-900 dark:text-white">Platform</h5>
          <p class="m-0 text-xs font-medium uppercase tracking-[0.16em] text-zinc-500">Visits</p>
        </div>
        @if (platformPieHasData()) {
        @if (platformPieOption(); as platformPie) {
        <div echarts [options]="platformPie" class="block h-80 w-full"></div>
        }
        } @else {
        <div
          class="flex h-80 items-center justify-center rounded-xl border border-dashed border-zinc-200 dark:border-white/10">
          <p class="m-0 text-sm text-zinc-500">No Android or iOS data for this period yet.</p>
        </div>
        }
      </section>
    </div>

    <section class="admin-stats-panel rounded-2xl p-5">
      <div class="mb-4 flex items-baseline justify-between gap-2">
        <h5 class="m-0 text-base font-semibold text-zinc-900 dark:text-white">Locations</h5>
        <p class="m-0 text-xs font-medium uppercase tracking-[0.16em] text-zinc-500">Visits</p>
      </div>
      @if (locationHasData()) {
      <div class="grid gap-6 lg:grid-cols-2">
        <div>
          <p class="m-0 mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">
            Top countries
          </p>
          <ul class="m-0 flex list-none flex-col gap-3 p-0">
            @for (country of locationCountries(); track country.country) {
            <li class="flex items-center gap-3">
              <p class="m-0 w-36 shrink-0 truncate text-sm text-zinc-800 dark:text-zinc-200">
                {{ country.country }}
              </p>
              <div class="h-2 min-w-0 flex-1 rounded-full bg-zinc-200 dark:bg-white/10" aria-hidden="true">
                <div class="h-2 rounded-full bg-sky-500" [style.width.%]="country.sharePercent"></div>
              </div>
              <p class="m-0 w-16 shrink-0 text-right text-sm tabular-nums text-zinc-700 dark:text-zinc-300">
                {{ country.activeUsers.toLocaleString() }}
              </p>
            </li>
            }
          </ul>
        </div>
        <div class="overflow-x-auto">
          <p class="m-0 mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">
            Top cities
          </p>
          <table class="w-full border-collapse text-left text-sm">
            <caption class="sr-only">Top cities by visits</caption>
            <thead>
              <tr class="border-b border-zinc-200 text-xs uppercase tracking-[0.12em] text-zinc-500 dark:border-white/10">
                <th scope="col" class="py-2 pr-3 font-semibold">City</th>
                <th scope="col" class="py-2 pr-3 font-semibold">Country</th>
                <th scope="col" class="py-2 text-right font-semibold">Visits</th>
              </tr>
            </thead>
            <tbody>
              @for (row of locationCities(); track row.country + row.city) {
              <tr class="border-b border-zinc-100 dark:border-white/5">
                <td class="py-2 pr-3 text-zinc-800 dark:text-zinc-200">{{ row.city || 'Unknown' }}</td>
                <td class="py-2 pr-3 text-zinc-600 dark:text-zinc-400">{{ row.country }}</td>
                <td class="py-2 text-right tabular-nums text-zinc-800 dark:text-zinc-200">
                  {{ row.activeUsers.toLocaleString() }}
                </td>
              </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
      } @else {
      <div
        class="flex h-40 items-center justify-center rounded-xl border border-dashed border-zinc-200 dark:border-white/10">
        <p class="m-0 text-sm text-zinc-500">No location data for this period yet.</p>
      </div>
      }
    </section>
    }
  </div>
</div>